<script setup lang="ts">
import { navigateTo } from '@/ts/management/view-navigation-management.ts';
import model from '@/ts/model.ts';
import SideNavFunClubIcon from '@/assets/icons/side-nav-fun-club.svg?raw';
import stellarStreamManagement, { type StellarStreamConfigItem } from '@/ts/management/stellar-stream-management.ts';

import { watch } from 'vue';
import gsap from 'gsap';
import vPreloadImg from '@/ts/directives/v-preload-img.ts';
import type { Star } from '@/ts/types/ui-animation-types.ts';
import { createStars, spawnStars } from '@/ts/management/ui-management.ts';
import { disposeWatchHandlers } from '@/ts/directives/watch-handlers';

const props = defineProps<{location: 'desktop-menu' | 'mobile-side-nav'}>();
const vLocation = {
    mounted(element: HTMLElement){
        element.classList.add(props.location);
    },
};
const vStellarStreamUiVisualUpdates = {
    unmounted: disposeWatchHandlers,
    mounted(element: HTMLElement){
        if(props.location != 'desktop-menu') return;

        const STAR_AMOUNT = 15;
        const stars: Star[] = [];

        createStars(STAR_AMOUNT, stars, element);

        let prevPoints: number | undefined;
        let t = 0;
        
        element.watchHandlers ||= [];
        element.watchHandlers.push(watch(() => model.keepAlive?.bonusPoints, points => {
            if(typeof prevPoints === 'undefined'){
                prevPoints = points;
                return;
            }

            if(!points) return;

            if(points > prevPoints){
                spawnStars(stars, (Math.PI * 2) / STAR_AMOUNT);
                element.classList.remove('bp-increase-anim');
                clearTimeout(t);
                t = setTimeout(() => element.classList.add('bp-increase-anim'), 10);
            }

            prevPoints = points;
        }, {immediate: true}));
    },
};

const vLevelReachedContainer = {
    unmounted: disposeWatchHandlers,
    mounted(element: HTMLElement){
        const elemLevelInfo = element.querySelector<HTMLElement>('.level-info');
        if(!elemLevelInfo) return;
        
        const STAR_AMOUNT = 15;
        const stars: Star[] = [];

        createStars(STAR_AMOUNT, stars, element);

        elemLevelInfo.style.opacity = '0';

        let tween: gsap.core.Tween | undefined;

        let prevLevelName: string | undefined;

        element.watchHandlers ||= [];
        element.watchHandlers.push(watch(() => stellarStreamManagement.currentLevelInfo.value, async levelInfo => {
            if(tween) tween.kill();

            const levelInfoName = levelInfo?.name;

            if(!prevLevelName){
                prevLevelName = levelInfoName;
                return;
            }

            if(prevLevelName == levelInfoName) return;
            prevLevelName = levelInfoName;

            const obj: Record<string, number> = {
                posY: -20,
                opacity: 0,
                scale: .5,
                brightness: 1,
            };

            await instanceGSAP(obj, {
                posY: 0,
                opacity: .35,
                duration: 1,
                onUpdate,
            })

            await instanceGSAP(obj, {
                opacity: .55,
                scale: 1,
                duration: .25,
                onUpdate,
            })

            obj.opacity = 1;
            obj.brightness = 10;

            spawnStars(stars, (Math.PI * 2) / STAR_AMOUNT);

            await instanceGSAP(obj, {
                scale: 1.1,
                brightness: 1,
                duration: 5,
                onUpdate,
            })

            await instanceGSAP(obj, {
                opacity: 0,
                duration: 1,
                onUpdate,
            })

            function onUpdate(){
                if(!elemLevelInfo) return;
                elemLevelInfo.style.transform = `translateY(${obj.posY}px) scale(${obj.scale})`;
                elemLevelInfo.style.opacity = obj.opacity + '';
                elemLevelInfo.style.filter = `brightness(${obj.brightness})`;
            }

            function instanceGSAP(obj: Record<string, unknown>, objTrans: Record<string, unknown>): Promise<boolean>{
                return new Promise(resolve => {
                    objTrans.onComplete = () => resolve(true);
                    tween = gsap.to(obj, objTrans);
                });
            }
        }));
    },
};

</script>

<template>
    <template v-if="model.bonusPointsType == 'stellar-stream'">
        <div class="stellar-stream-menu-ui" v-location v-stellar-stream-ui-visual-updates>
            <template v-if="location == 'mobile-side-nav'">
                <div class="icon" v-html="SideNavFunClubIcon"></div>
            </template>
            <button 
                class="btn" 
                v-mobile 
                v-location 
                @click.prevent="navigateTo('user-stellar-stream')"
            >
                {{ stellarStreamManagement.points }}&nbsp;STARS
            </button>
            <template v-if="location == 'desktop-menu'">
                <div class="level-reached-container" v-level-reached-container>
                        <div class="level-info" :style="{'--color': stellarStreamManagement.currentLevelInfo.value?.color}">
                            <template v-if="stellarStreamManagement.currentLevelInfo.value">
                                <div class="bg" v-preload-img="stellarStreamManagement.currentLevelInfo.value.imgSrc"></div>
                                <div class="text">
                                    {{ stellarStreamManagement.currentLevelInfo.value.name }}
                                </div>
                            </template>
                        </div>
                </div>
            </template>
        </div>
    </template>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    @use 'sass:list';
    .stellar-stream-menu-ui{
        position: relative;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        & > .level-reached-container{
            position: absolute;
            top: calc(100% + 10px);
            left:-30px;
            pointer-events: none;
            width: 160px;
            height: 90px;
            & > .level-info{
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                & > .bg{
                    position: absolute;
                    background-position: center;
                    background-size: contain;
                    background-repeat: no-repeat;
                    width: inherit;
                    height: inherit;
                    top:0;
                    left:0;
                    z-index: 0;
                    filter: drop-shadow(0 0 5px var(--color));
                }
                & > .text{
                    position: relative;
                    z-index: 1;
                    text-transform: uppercase;
                    font-size: 20px;
                    font-weight: bold;
                    text-shadow: 0 0 5px #000;
                }
            }
        }
        & :deep(.star){
            position: absolute;
            left: calc(50% - 15px);
            top: calc(50% - 7px);
            pointer-events: none;
            opacity: 0;
            &,
            & > *,
            & > * > *{
                display: flex;
                justify-content: center;
                align-items: center;
                width: 15px;
                height: 15px;
                color: #fff;
                filter: drop-shadow(0 0 15px #ffffff42);
            }
        }
        &.mobile-side-nav{
            margin-bottom: 5px;
        }
        &.desktop-menu{
            margin-right: 15px;
            &.bp-increase-anim > .btn{
                animation-fill-mode: forwards;
                animation-duration: 3s;
                animation-name: bp-increase-anim;
                
                $colors: (
                    #2989d8,
                    #59ff88,
                    #2989d8,
                    #ff4c4f,
                    #ffd800,
                );

                @keyframes bp-increase-anim {
                    0% {
                        transform: scale(1.1);
                        background-color: #ffffff80;
                        filter: drop-shadow(0 0 2px list.nth($colors, 1))
                                drop-shadow(0 0 6px list.nth($colors, 1));
                    }

                    @for $i from 2 through list.length($colors) {
                        #{($i - 1) * 20%} {
                            $color: list.nth($colors, $i);

                            filter: drop-shadow(0 0 2px $color)
                                    drop-shadow(0 0 6px $color);
                        }
                    }

                    100% {
                        transform: scale(1);
                        background-color: transparent;
                        filter: drop-shadow(0 0 2px transparent)
                                drop-shadow(0 0 6px transparent);
                    }
                }
            }
        }
        & > .icon{
            display: flex;
            justify-content: center;
            align-items: center;
            @include shared.site() using($site-key){
                color: shared.theme-color('comp3_3', $site-key);
            }
        }
        & > .btn{
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: transparent;
            cursor: pointer;
            color: #fff;
            border: none;
            &.desktop-menu{
                padding: 2px 7px;
                font-size: 12px;
                font-weight: 600;
                font-family: Poppins, sans-serif;
                border-radius: 5px;
                box-shadow: 0 0 0 1.5px #fff;
                transition: color .25s, box-shadow .25s;
                &:not([data-mobile]):hover{
                    @include shared.site() using($site-key){
                        $color: shared.theme-color('comp3_3', $site-key);
                        color: $color;
                        box-shadow: 0 0 0 1.5px $color;
                    }
                }
            }
            &.mobile-side-nav{
                @include shared.casino-section-title();
            }
        }
    }
</style>